import { DefaultTheme, createGlobalStyle, css } from 'styled-components';

export const Breakpoints = {
  min: {
    phone: 400,
    tablet: 768,
    smallDesktop: 992,
    mediumDesktop: 1230,
    largeDesktop: 1440,
  },
  max: {
    smallPhone: 399,
    phone: 767,
    tablet: 991,
    smallDesktop: 1229,
    mediumDesktop: 1439,
  },
} as const;

const typography = {
  body: {
    fontFamily:
      '-apple-system, BlinkMacSystemFont, Helvetica, Arial, sans-serif',
    fontWeight: 400,
    fontSize: `14px`,
    lineHeight: `22px`,
  },
  heading1: {
    fontFamily: "'Manrope', sans-serif",
    fontWeight: 700,
    fontSize: '32px',
    lineHeight: '42px',
  },
  heading2: {
    fontFamily: "'Manrope', sans-serif",
    fontWeight: 700,
    fontSize: '24px',
    lineHeight: '36px',
  },
  button: {
    fontFamily: "'Manrope', sans-serif",
    fontWeight: 700,
    fontSize: '16px',
    lineHeight: '24px',
  },
  caption: {
    fontFamily: "'Manrope', sans-serif",
    fontWeight: 700,
    fontSize: '14px',
    lineHeight: '24px',
  },
  captionSemibold: {
    fontFamily: "'Manrope', sans-serif",
    fontWeight: 600,
    fontSize: '14px',
    lineHeight: '24px',
  },
  smallBold: {
    fontFamily: "'Manrope', sans-serif",
    fontWeight: 700,
    fontSize: '12px',
    lineHeight: '16px',
  },
  smallText: {
    fontFamily: "'Manrope', sans-serif",
    fontWeight: 500,
    fontSize: '12px',
    lineHeight: '18px',
  },
  smallHeadline: {
    fontFamily: "'Manrope', sans-serif",
    fontWeight: 700,
    fontSize: '11px',
    lineHeight: '15px',
  },
  smallMediumBold: {
    fontFamily: "'Manrope', sans-serif",
    fontWeight: 600,
    fontSize: '11px',
    lineHeight: '18px',
  },
  smallMedium: {
    fontFamily: "'Manrope', sans-serif",
    fontWeight: 500,
    fontSize: '11px',
    lineHeight: '15px',
  },
  smallNormal: {
    fontFamily: "'Manrope', sans-serif",
    fontWeight: 400,
    fontSize: '11px',
    lineHeight: '15px',
  },
  small: {
    fontFamily: "'Manrope', sans-serif",
    fontWeight: 500,
    fontSize: '10px',
    lineHeight: '14px',
  },
  extraSmall: {
    fontFamily: "'Manrope', sans-serif",
    fontWeight: 700,
    fontSize: '9px',
    lineHeight: '12px',
  },
  extraSmallSemiBold: {
    fontFamily: "'Manrope', sans-serif",
    fontWeight: 600,
    fontSize: '9px',
    lineHeight: '12px',
  },
  superSmall: {
    fontFamily: "'Manrope', sans-serif",
    fontWeight: 700,
    fontSize: '6px',
    lineHeight: '12px',
  },
};

const colors = {
  blue: '#0D1C49',
  lightBlue: '#136BD7',
  purple: '#4136CF',
  purpleLighter: '#5D54D6',
  purpleLight: '#9F9AE6',
  purpleExtraLight: 'rgba(159, 154, 230, 0.5)',
  black: '#222222',
  darkGray: '#9C9C9C',
  gray: '#909090',
  grayscale: '#F2F3F7',
  grayscaleDark: '#f1f1f1',
  grayscaleLight: '#F8F8F8',
  grayscaleExtraLight: '#fbfbfb',
  grayscaleSuperLight: '#F9F9F9',
  grayscaleSuperExtraLight: '#FaFaFa',
  lightGray: '#E9E9E9',
  white: '#ffffff',
  success: '#42BE77',
  successLight: '#F0FAF5',
  warning: '#F0AD1C',
  warningLight: '#FFF8E8',
  info: '#1789D5',
  infoLight: '#D1E7F7',
  productionInfoLight: '#EDF5FC',
  error: '#F64629',
  errorLight: '#FFF4F2',
  research: '#C9513D',
  researchLight: '#FBF1F0',
  production: '#ED911B',
  productionLight: '#FEF7ED',
  postProduction: '#AD64C5',
  postProductionLight: '#F9F3FB',
  optimisation: '#1C6DD0',
  optimisationLight: '#EDF4FC',
  distribution: '#18713D',
  distributionLight: '#EDF4F0',
  distributionPurple: '#513252',
  distributionOrange: '#F4743B',
  distributionPurpleLight: 'rgba(81, 50, 82, 0.08)',
};

const breakpoints = {
  phone: `@media(max-width: ${Breakpoints.max.phone}px)`,
  tablet: `@media(max-width: ${Breakpoints.max.tablet}px)`,
  smallDesktop: `@media(max-width: ${Breakpoints.max.smallDesktop}px)`,
  mediumDesktop: `@media(max-width: ${Breakpoints.max.mediumDesktop}px)`,
};

const zIndex = {
  2: 2,
  5: 5,
  10: 10,
  20: 20,
  50: 50,
};

const theme: DefaultTheme = {
  typography,
  colors,
  breakpoints,
  zIndex,
};

export default theme;

export type Color = keyof DefaultTheme['colors'];
export type Typography = keyof DefaultTheme['typography'];

export const GlobalStyles = createGlobalStyle`
  body {
    background-color:${({ theme }) => theme.colors.white};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
  *, &::after, &::before {
    box-sizing: border-box;
    font-family: inherit;
  }
  
  a {
    color: inherit;
    text-decoration: none;
  }
  button {
    background-color: transparent;
    border: 0;
    padding: 0;
    cursor: pointer;
  }
  .react-select__menu-portal {
    z-index: 10 !important;
    .react-select__option,
    .react-select__menu-notice {
      min-height: 32px;
      height: 32px;
      text-align: center;
      cursor: auto;
      justify-content: center;
    }
    .react-select__menu {
      z-index: 10;
    }
    &.react-select--is-disabled {
    pointer-events: auto;

    .react-select__control {
      cursor: not-allowed;
      background: ${({ theme }) => theme.colors.grayscaleLight};
    }
  }
  .react-select__control {
    box-shadow: none !important;
    border-radius: 4px;
    height: 46px;

    ${({ theme }) => css`
      border: 1px solid
      font-family: ${theme.typography.captionSemibold.fontFamily};
      font-size: ${theme.typography.captionSemibold.fontSize};
      font-weight: ${theme.typography.captionSemibold.fontWeight};
      &:hover,
      &--is-focused {
        border-color: ${theme.colors.gray};
      }
    `}
    &--menu-is-open {
      .react-select__indicator {
        transform: rotate(180deg);
      }
    }
  }
  .react-select__indicator-separator {
    display: none;
  }
  .react-select__indicator {
    transition: 0.2s ease-out transform;
    color: ${({ theme }) => theme.colors.black};
  }
  .react-select__value-container {
    padding: 0 16px;
  }
  .react-select__single-value {
    color: ${({ theme }) => theme.colors.black};
    margin: 0;
  }

  .react-select__menu {
    border: 0;
    padding: 4px;
    background: ${({ theme }) => theme.colors.white};
    border-radius: 4px;
    box-shadow: 0px 0px 8px rgba(37, 40, 43, 0.12);
  }

  .react-select__option,
  .react-select__menu-notice {
    padding: 9px 12px;
    border: 0;
    border-radius: 4px;
    margin: 0 0 4px;
    display: flex;
    min-height: 30px;
    align-items: center;
    cursor: pointer;
    ${({ theme }) => css`
      background: ${theme.colors.white};
      font-family: ${theme.typography.captionSemibold.fontFamily};
      font-size: 12px;
      line-height: 22px;
      font-weight: ${theme.typography.captionSemibold.fontWeight};
      color: ${theme.colors.gray};
      &:hover,
      &--is-selected {
        background: ${theme.colors.grayscaleLight};
        color: ${theme.colors.black};
      }
    `}
    &:last-of-type {
      margin: 0;
    }
  }
  .lightbox {
    --yarl__color_backdrop: rgba(0, 0, 0, 0.8);
  }
  .yarl__button {
    filter: none;
  }
  .yarl__slide {
    padding: 54px 86px;
  }
  }
`;

export const scrollBarStyles = css`
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
    background-color: transparent;
  }
  ::-webkit-scrollbar-thumb {
    border-radius: 8px;
    background-color: ${({ theme }) => theme.colors.lightGray};
    border: solid 2px ${({ theme }) => theme.colors.white};
  }
  ::-webkit-scrollbar-track {
    background-color: transparent;
  }
`;

export const hexToRgba = (hex: string, alpha = 1) => {
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  const newHex = hex.replace(shorthandRegex, function (m, r, g, b) {
    return r + r + g + g + b + b;
  });

  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(newHex);

  if (!result) return ``;

  return `rgba(${parseInt(result[1], 16)}, ${parseInt(
    result[2],
    16,
  )}, ${parseInt(result[3], 16)}, ${alpha})`;
};
